import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Mail,
  Clock,
  Facebook,
Linkedin,
ArrowRight,
  Send,
  CalendarClock,
  Phone,
} from "lucide-react";
import { SERVICES, saveNewsletter } from "../data/mock";
import { toast } from "sonner";
import { useQuote } from "./QuoteModal";
import logo from "../assets/logo";

const CONTACT = {
  name: "Abhi Insurance Agency Inc.",
  address: "8128 128 St #302, Surrey, BC V3W 1R1",
  phone: "(604) 996 6862",
  phoneHref: "tel:+16049966862",
  email: "info@abhifinancial.com",
  hours: "Open 7 days a week, 9:00 to 17:00",
};

const FOOTER_DISCLAIMER =
  "Licensed insurance advisory in British Columbia. Abhi Insurance Agency Inc. is not an insurer. Policies are underwritten by licensed Canadian insurance companies and coverage is subject to the terms of the policy issued.";

const COMPANY_LINKS = [
  { label: "About Us", path: "/about" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Use", path: "/terms-of-use" },
];
const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/Abhiinsuranceagency/",
    icon: Facebook,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/abhi-insurance-agency-inc/?originalSubdomain=ca",
    icon: Linkedin,
  },
];
function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="inline-block py-1 text-[13px] leading-relaxed text-[#D4E7F1] transition-colors duration-200 hover:text-white hover:underline hover:underline-offset-4 sm:text-[14px]"
    >
      {children}
    </Link>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { openQuote } = useQuote();

  const subscribe = async (event) => {
    event.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || submitting) return;

    setSubmitting(true);

    try {
      await saveNewsletter(trimmedEmail);
      setEmail("");

      toast.success("Subscribed!", {
        description: "You'll receive our latest insurance insights.",
      });
    } catch {
      toast.error("Unable to subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#073B67] text-[#D4E7F1]">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10">
        {/* Main footer: one continuous surface */}
        <div className="grid grid-cols-1 gap-8 py-8 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-7 lg:grid-cols-[1.35fr_1fr_0.7fr_1.15fr] lg:gap-8 lg:py-9">
          {/* Brand and contact details */}
          <div className="min-w-0">
            <Link
              to="/"
              aria-label="Abhi Insurance Agency home"
              className="inline-flex rounded-lg bg-white px-3 py-2"
            >
              <img
                src={logo}
                alt="Abhi Insurance Agency"
                className="h-10 w-auto"
              />
            </Link>

            <p className="mt-4 font-heading text-[14px] font-bold text-white">
              {CONTACT.name}
            </p>

           <address className="mt-3 space-y-2.5 not-italic max-sm:mx-auto max-sm:w-full max-sm:max-w-[280px]">
 {/* Address */}
<a
  href="https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Open Abhi Insurance Agency location in Google Maps"
  className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5 text-left text-[13px] leading-relaxed transition-colors hover:text-white hover:sm:flex"
>
  <MapPin
    aria-hidden="true"
    className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]"
  />

  <span className="min-w-0">
    {CONTACT.address}
  </span>
</a>

  {/* Phone */}
  <a
    href={CONTACT.phoneHref}
    className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5 text-left text-[13px] leading-relaxed transition-colors hover:text-white sm:flex"
  >
    <Phone
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]"
    />

    <span className="min-w-0">Phone: {CONTACT.phone}</span>
  </a>

  {/* Email */}
  <a
    href={`mailto:${CONTACT.email}`}
    className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5 text-left text-[13px] leading-relaxed transition-colors hover:text-white sm:flex"
  >
    <Mail
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]"
    />

    <span className="min-w-0 break-words">
      Email: {CONTACT.email}
    </span>
  </a>

  {/* Opening hours */}
  <div className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5 text-left sm:flex">
    <Clock
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0 text-[#B9DFF3]"
    />

    <span className="min-w-0 text-[13px] leading-relaxed">
      {CONTACT.hours}
    </span>
  </div>
</address>
          </div>

          {/* Navigation stays side by side on mobile */}
          <div className="grid min-w-0 grid-cols-[1.2fr_1fr] gap-5 sm:gap-6 lg:col-span-2 lg:grid-cols-[1fr_0.7fr] lg:gap-8">
            <nav aria-label="Footer services" className="min-w-0">
              <h2 className="font-heading text-[15px] font-bold text-white">
                Services
              </h2>

              <ul className="mt-3 space-y-1">
                {SERVICES.map((service) => (
                  <li key={service.slug}>
                    <FooterLink to={`/services/${service.slug}`}>
                      {service.title}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Footer company" className="min-w-0">
              <h2 className="font-heading text-[15px] font-bold text-white">
                Company
              </h2>

              <ul className="mt-3 space-y-1">
                {COMPANY_LINKS.map(({ label, path }) => (
                  <li key={path}>
                    <FooterLink to={path}>{label}</FooterLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Newsletter and consultation */}
          <div className="min-w-0 sm:col-span-2 lg:col-span-1">
            <h2 className="font-heading text-[15px] font-bold text-white">
              Insurance Insights
            </h2>

            <p className="mt-3 text-[13px] leading-relaxed text-[#D4E7F1]">
              Subscribe for practical guides on Super Visa, life and
              health coverage in Canada.
            </p>

            <form
              onSubmit={subscribe}
              aria-busy={submitting}
              className="mt-4 flex items-center overflow-hidden rounded-lg border border-white/25 bg-white/[0.06] p-1 transition-colors focus-within:border-[#B9DFF3]"
            >
              <input
                type="email"
                name="newsletterEmail"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Your email address"
                aria-label="Your email address"
                data-testid="newsletter-email"
                className="h-9 min-w-0 flex-1 bg-transparent px-3 text-[13px] text-white outline-none placeholder:text-[#A8CCDE]"
              />

              <button
                type="submit"
                disabled={submitting}
                aria-label={submitting ? "Subscribing" : "Subscribe"}
                data-testid="newsletter-submit"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-[#B9DFF3] text-[#073B67] transition-colors hover:bg-white disabled:cursor-wait disabled:opacity-60"
              >
                <Send aria-hidden="true" className="h-4 w-4" />
              </button>
            </form>

            <p className="mt-2 text-[11px] leading-relaxed text-[#A8CCDE]">
              By subscribing you agree to our data handling terms.
            </p>

            <button
              type="button"
              onClick={() => openQuote()}
              data-testid="footer-schedule-call"
              className="group mt-4 inline-flex items-center gap-2 rounded-lg bg-[#0785C7] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#006FB9]"
            >
              <CalendarClock
                aria-hidden="true"
                className="h-4 w-4 shrink-0"
              />
              Schedule a Call
              <ArrowRight
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              />
            </button>
          </div>
        </div>

        {/* Plain disclaimer: no separate card */}
        <div className="border-t border-white/15 pt-4">
          <p className="text-[12px] leading-relaxed text-[#A8CCDE]">
            {FOOTER_DISCLAIMER}
          </p>
        </div>

        {/* Compact bottom row */}
        <div className="flex flex-col-reverse items-start gap-3 pb-5 pt-4 sm:flex-row sm:items-center sm:justify-between">
         <p className="text-[12px] leading-relaxed text-[#A8CCDE]">
  &copy; Abhi Insurance Agency Inc. All rights reserved.
</p>

          <div
            aria-label="Social media"
            className="flex shrink-0 items-center gap-2"
          >
            {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="grid h-8 w-8 place-items-center rounded-full bg-white/[0.08] text-[#D4E7F1] transition-colors hover:bg-[#B9DFF3] hover:text-[#073B67]"
              >
                <Icon aria-hidden="true" className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}